/**
 * Moving averages: simple, weighted and exponential, trailing or centred.
 *
 * Every window average is a *kernel*: weights on the observations at fixed offsets from
 * time t, normalised to sum to one.
 *
 * - **Simple, trailing**: the last k observations, equally weighted.
 * - **Weighted, trailing**: the last k observations with weights wᵢ (oldest first; the
 *   default 1, 2, … k makes the newest count k times the oldest).
 * - **Centred**: the window straddles t. An odd k is symmetric. An even k has no middle
 *   observation, so a simple average uses the classical 2×k moving average (half weight
 *   on the two end points, k + 1 points in all — R's `filter(x, c(.5, rep(1, k − 1), .5)/k)`),
 *   and a weighted one takes k/2 points before t and k/2 − 1 after.
 * - **Exponential**: sₜ = α·yₜ + (1 − α)·sₜ₋₁, with α = 2/(N + 1) for a "span" of N. It can
 *   start from the first observation (pandas' `adjust = False`, R's recursive `filter`) or,
 *   as in technical-analysis charts, from the simple average of the first N.
 *
 * Where the window runs off the data, either the value is left blank or (with `partial`)
 * the average of the points that are there is shown. The tests pin each kind to R's
 * `stats::filter`.
 */

export type MovingAverageType = "simple" | "weighted" | "exponential";
export type Alignment = "trailing" | "centred";
export type EmaSeed = "first" | "average";

export interface MovingAverageOptions {
  type: MovingAverageType;
  /** Window size k (or the span N for an exponential average); a whole number ≥ 1. */
  window: number;
  alignment: Alignment;
  /** Weights for a weighted average, oldest first; defaults to 1, 2, … k. */
  weights?: readonly number[];
  /** α for an exponential average; defaults to 2 / (window + 1). */
  alpha?: number;
  emaSeed?: EmaSeed;
  /** Average whatever points exist at the ends instead of leaving blanks. */
  partial?: boolean;
}

export interface KernelPoint {
  offset: number;
  weight: number;
}

/** The weights on each offset from t for a simple or weighted average. */
export function kernel(options: MovingAverageOptions): KernelPoint[] {
  const k = options.window;
  const weights =
    options.type === "weighted"
      ? (options.weights ?? Array.from({ length: k }, (_, i) => i + 1))
      : new Array<number>(k).fill(1);
  if (options.alignment === "trailing") {
    return weights.map((weight, i) => ({ offset: i - (k - 1), weight }));
  }
  if (k % 2 === 1) {
    const half = (k - 1) / 2;
    return weights.map((weight, i) => ({ offset: i - half, weight }));
  }
  if (options.type === "simple") {
    return Array.from({ length: k + 1 }, (_, i) => ({
      offset: i - k / 2,
      weight: i === 0 || i === k ? 0.5 : 1,
    }));
  }
  return weights.map((weight, i) => ({ offset: i - k / 2, weight }));
}

function windowAverage(
  values: readonly number[],
  points: readonly KernelPoint[],
  partial: boolean
): (number | null)[] {
  return values.map((_, t) => {
    let total = 0;
    let used = 0;
    let missing = false;
    for (const { offset, weight } of points) {
      const index = t + offset;
      if (index < 0 || index >= values.length) {
        missing = true;
      } else {
        total += weight * values[index];
        used += weight;
      }
    }
    if ((missing && !partial) || used === 0) {
      return null;
    }
    return total / used;
  });
}

/** α for a span of N observations. */
export function alphaFromSpan(span: number): number {
  return 2 / (span + 1);
}

function exponential(
  values: readonly number[],
  alpha: number,
  seed: EmaSeed,
  span: number
): (number | null)[] {
  const result = new Array<number | null>(values.length).fill(null);
  let start = 0;
  let previous: number;
  if (seed === "average" && span >= 1 && span <= values.length) {
    start = span - 1;
    previous = values.slice(0, span).reduce((a, b) => a + b, 0) / span;
  } else {
    previous = values[0];
  }
  result[start] = previous;
  for (let t = start + 1; t < values.length; t++) {
    previous = alpha * values[t] + (1 - alpha) * previous;
    result[t] = previous;
  }
  return result;
}

/** The moving average of `values`, with null where the window is incomplete. */
export function movingAverage(
  values: readonly number[],
  options: MovingAverageOptions
): (number | null)[] {
  if (values.length === 0 || options.window < 1) {
    return values.map(() => null);
  }
  if (options.type === "exponential") {
    return exponential(
      values,
      options.alpha ?? alphaFromSpan(options.window),
      options.emaSeed ?? "first",
      options.window
    );
  }
  return windowAverage(values, kernel(options), options.partial ?? false);
}

export interface ForecastAccuracy {
  /** Number of one-step-ahead forecasts scored. */
  n: number;
  mae: number;
  rmse: number;
  /** Mean absolute percentage error; NaN when an actual value is zero. */
  mape: number;
}

/**
 * Score the moving average as a one-step-ahead forecast: the average up to t − 1
 * predicts yₜ. Meaningful for trailing and exponential averages, which only use the past.
 */
export function forecastAccuracy(
  values: readonly number[],
  averages: readonly (number | null)[]
): ForecastAccuracy | null {
  let n = 0;
  let abs = 0;
  let squared = 0;
  let percent = 0;
  let zero = false;
  for (let t = 1; t < values.length; t++) {
    const forecast = averages[t - 1];
    if (forecast === null) {
      continue;
    }
    const error = values[t] - forecast;
    n += 1;
    abs += Math.abs(error);
    squared += error * error;
    if (values[t] === 0) {
      zero = true;
    } else {
      percent += Math.abs(error / values[t]);
    }
  }
  if (n === 0) {
    return null;
  }
  return {
    n,
    mae: abs / n,
    rmse: Math.sqrt(squared / n),
    mape: zero ? Number.NaN : (100 * percent) / n,
  };
}

/** The last defined average: the forecast for the next period from a trailing average. */
export function latestAverage(
  averages: readonly (number | null)[]
): number | null {
  for (let t = averages.length - 1; t >= 0; t--) {
    const value = averages[t];
    if (value !== null) {
      return value;
    }
  }
  return null;
}
